import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    CustomAuthPublicClientApplication,
    type ICustomAuthPublicClientApplication,
    SignUpCodeRequiredState,
    SignUpPasswordRequiredState,
    SignUpCompletedState,
    type AuthFlowStateBase,
    type PopupRequest,
} from '@azure/msal-browser/custom-auth';
import { customAuthConfig } from '../../nativeAuthConfig';
import { authStyles, sanitizeInput, isValidEmail } from './authStyles';
import { NATIVE_AUTH_SESSION_KEY } from '../../components/LogoutButton';
import { completeNativeAuthSession } from '../../utils/nativeAuthComplete';
import {
    LEGAL_DOCUMENTS,
    MANDATORY_DOCUMENT_TYPES,
    DOCUMENT_NAMES,
    PROFILING_CONSENT_VERSION,
    getDocumentPath,
} from '../../config/legalDocuments';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { config } from '../../config';

type Step = 'details' | 'password' | 'otp' | 'legal' | 'complete';

export default function RegisterPage() {
    const navigate = useNavigate();
    const [authClient, setAuthClient] = useState<ICustomAuthPublicClientApplication | null>(null);
    const [step, setStep] = useState<Step>('details');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [focusedField, setFocusedField] = useState<string | null>(null);
    const [signUpState, setSignUpState] = useState<AuthFlowStateBase | null>(null);

    // Step 1: Details
    const [email, setEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [emailError, setEmailError] = useState('');
    const [nameError, setNameError] = useState('');

    // Step 2: Legal acceptance
    const [acceptedMandatory, setAcceptedMandatory] = useState<Record<string, boolean>>({
        tos: false,
        privacy: false,
        ai_notice: false,
    });
    const [profilingConsent, setProfilingConsent] = useState(false);

    // Step 3: Password
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    // Step 4: OTP
    const [otpCode, setOtpCode] = useState('');

    // Document overlay
    const [viewingDoc, setViewingDoc] = useState<string | null>(null);
    const [docContent, setDocContent] = useState<string>('');
    const [docLoading, setDocLoading] = useState(false);

    // Initialize native auth client
    useEffect(() => {
        const init = async () => {
            try {
                const instance = await CustomAuthPublicClientApplication.create(customAuthConfig);
                setAuthClient(instance);

                const account = instance.getCurrentAccount();
                if (account.isCompleted()) {
                    window.location.href = '/';
                }
            } catch (err: any) {
                setError('Nepodarilo sa pripojiť k autentifikačnej službe.');
            }
        };
        init();
    }, [navigate]);

    // Validate email format on blur
    const handleEmailBlur = () => {
        setFocusedField(null);
        const cleanEmail = sanitizeInput(email.trim().toLowerCase(), 254);
        if (cleanEmail && !isValidEmail(cleanEmail)) {
            setEmailError('E-mailová adresa nie je v správnom tvare.');
        }
    };

    // Validate nickname on blur
    const handleNameBlur = () => {
        setFocusedField(null);
        if (displayName.trim().length === 0 && displayName.length > 0) {
            setNameError('Zadajte prezývku.');
        }
    };

    // Step 1: Validate details and go to legal
    const handleDetailsSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const cleanEmail = sanitizeInput(email.trim().toLowerCase(), 254);
        const cleanName = sanitizeInput(displayName.trim(), 100);

        // Validate fields
        let hasError = false;
        if (!cleanEmail) { setEmailError('Zadajte e-mailovú adresu.'); hasError = true; }
        else if (!isValidEmail(cleanEmail)) { setEmailError('E-mailová adresa nie je v správnom tvare.'); hasError = true; }
        if (!cleanName) { setNameError('Zadajte prezývku.'); hasError = true; }

        // Block if there's any existing email error (e.g. format error from blur)
        if (emailError) hasError = true;

        if (hasError) return;

        setEmail(cleanEmail);
        setDisplayName(cleanName);
        setStep('password');
    };

    // Step 2: Legal acceptance -> start sign-up
    const allMandatoryAccepted = MANDATORY_DOCUMENT_TYPES.every(t => acceptedMandatory[t]);

    const handleLegalSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!allMandatoryAccepted) {
            setError('Pre registráciu musíte súhlasiť so všetkými povinnými dokumentmi.');
            return;
        }
        setError('');
        if (signUpState instanceof SignUpCompletedState) {
            await handleSignUpComplete(signUpState);
        }
    };

    // Step 2: Password -> call signUp API
    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setPasswordError('');

        if (!password) { setPasswordError('Zadajte heslo.'); return; }
        if (password.length < 8) { setPasswordError('Heslo musí mať aspoň 8 znakov.'); return; }
        if (password !== confirmPassword) { setPasswordError('Heslá sa nezhodujú.'); return; }

        setLoading(true);

        if (!authClient) return;

        try {
            const result = await authClient.signUp({
                username: email,
                password,
                attributes: {
                    displayName,
                },
            });

            if (result.isFailed()) {
                if (result.error?.isUserAlreadyExists()) {
                    setError('Účet s touto e-mailovou adresou už existuje. Chcete sa prihlásiť?');
                } else if (result.error?.isInvalidPassword()) {
                    setPasswordError('Heslo nespĺňa požiadavky. Skúste silnejšie heslo (veľké a malé písmená, čísla).');
                } else if (result.error?.isInvalidUsername()) {
                    setError('Neplatná e-mailová adresa.');
                    setStep('details');
                } else {
                    setError(getErrorMessage(result.error));
                }
                setLoading(false);
                return;
            }

            setSignUpState(result.state);

            // SDK may ask for password separately
            if (result.state instanceof SignUpPasswordRequiredState) {
                // Already provided password in signUp call, submit it
                const pwResult = await result.state.submitPassword(password);
                if (pwResult.isFailed()) {
                    setPasswordError('Heslo nespĺňa požiadavky.');
                    setLoading(false);
                    return;
                }
                setSignUpState(pwResult.state);
                if (pwResult.state instanceof SignUpCodeRequiredState) {
                    setStep('otp');
                } else if (pwResult.state instanceof SignUpCompletedState) {
                    await handleSignUpComplete(pwResult.state);
                    return;
                }
            } else if (result.state instanceof SignUpCodeRequiredState) {
                setStep('otp');
            } else if (result.state instanceof SignUpCompletedState) {
                await handleSignUpComplete(result.state);
                return;
            }
        } catch (err: any) {
            setError('Nastala chyba pri registrácii. Skúste to znova.');
        }
        setLoading(false);
    };

    // Step 4: Submit OTP code
    const handleOtpSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!otpCode || otpCode.length < 4) {
            setError('Zadajte overovací kód z e-mailu.');
            return;
        }

        setLoading(true);

        try {
            if (signUpState instanceof SignUpCodeRequiredState) {
                const result = await signUpState.submitCode(otpCode);

                if (result.isFailed()) {
                    if (result.error?.isInvalidCode()) {
                        setError('Neplatný overovací kód. Skontrolujte e-mail a skúste znova.');
                    } else {
                        setError(getErrorMessage(result.error));
                    }
                    setLoading(false);
                    return;
                }

                if (result.state instanceof SignUpCompletedState) {
                    setSignUpState(result.state);
                    setStep('legal');
                    setLoading(false);
                    return;
                }

                setSignUpState(result.state);
            }
        } catch (err: any) {
            setError('Nastala chyba pri overovaní kódu. Skúste to znova.');
        }
        setLoading(false);
    };

    // Handle completed sign-up: record legal acceptance, auto sign-in
    const handleSignUpComplete = async (completedState: SignUpCompletedState, acceptAll: boolean = false) => {
        setStep('complete');
        const includesProfiling = profilingConsent || acceptAll;

        try {
            // Auto sign-in
            const signInResult = await completedState.signIn();

            if (signInResult.isCompleted() && signInResult.data?.account?.idToken) {
                const token = signInResult.data.account.idToken;

                // Record legal document acceptance (batch format)
                const docTypes = [...MANDATORY_DOCUMENT_TYPES];
                const versions: Record<string, string> = {};
                for (const dt of MANDATORY_DOCUMENT_TYPES) {
                    versions[dt] = LEGAL_DOCUMENTS[dt].currentVersion;
                }
                if (includesProfiling) {
                    docTypes.push('profiling' as any);
                    versions['profiling'] = PROFILING_CONSENT_VERSION;
                }

                try {
                    const resp = await fetch(`${config.apiUrl}/acceptLegalDocument`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Auth-Token': `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            documentTypes: docTypes,
                            versions,
                        }),
                    });
                    if (!resp.ok) {
                        console.error('Legal acceptance failed:', resp.status, await resp.text());
                    }
                } catch (err) {
                    console.error('Legal acceptance request failed:', err);
                }

                // Navigate to main app
                await completeNativeAuthSession(email);
                return;
            }

            // If auto sign-in failed, redirect to login
            setError('Registrácia úspešná! Teraz sa môžete prihlásiť.');
            setTimeout(() => navigate('/auth/login', { replace: true }), 2000);
        } catch (err) {
            setError('Registrácia úspešná! Teraz sa môžete prihlásiť.');
            setTimeout(() => navigate('/auth/login', { replace: true }), 2000);
        }
    };

    // Google registration via browser-delegated flow (popup)
    const handleGoogleRegister = async () => {
        if (!authClient) return;
        setError('');
        setLoading(true);

        try {
            const popUpRequest: PopupRequest = {
                authority: customAuthConfig.auth.authority,
                scopes: [],
                redirectUri: customAuthConfig.auth.redirectUri || '/',
                prompt: 'login',
            };

            await authClient.loginPopup(popUpRequest);

            const accountResult = authClient.getCurrentAccount();
            if (accountResult.isCompleted()) {
                // Google users will go through LegalConsentModal on first login via existing flow
                await completeNativeAuthSession();
                return;
            }

            setError('Registrácia cez Google zlyhala. Skúste to znova.');
        } catch (err: any) {
            if (err?.errorCode === 'user_cancelled') {
                setError('');
            } else {
                setError('Registrácia cez Google zlyhala. Skúste to znova.');
            }
        }
        setLoading(false);
    };

    const getErrorMessage = (error: any): string => {
        const desc = error?.errorData?.errorDescription;
        if (desc?.includes('throttl') || desc?.includes('rate')) return 'Príliš veľa pokusov. Počkajte minútu a skúste znova.';
        return 'Nastala neočakávaná chyba. Skúste to znova.';
    };

    const getInputStyle = (field: string, hasError: boolean = false) => ({
        ...authStyles.input,
        ...(hasError ? authStyles.inputError : {}),
        ...(focusedField === field && !hasError ? authStyles.inputFocused : {}),
    });

    // Open legal document in overlay
    const openDocument = async (docType: string) => {
        setViewingDoc(docType);
        setDocLoading(true);
        setDocContent('');
        try {
            const url = getDocumentPath(docType);
            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to load');
            const text = await response.text();
            setDocContent(text);
        } catch {
            setDocContent('Dokument sa nepodarilo načítať.');
        }
        setDocLoading(false);
    };

    // Document overlay
    const renderDocumentOverlay = () => {
        if (!viewingDoc) return null;
        const docName = DOCUMENT_NAMES[viewingDoc] || viewingDoc;
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.6)',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                }}
                onClick={() => setViewingDoc(null)}
            >
                <div
                    style={{
                        background: '#fff',
                        borderRadius: '16px',
                        width: '100%',
                        maxWidth: '680px',
                        maxHeight: '85vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexShrink: 0,
                    }}>
                        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#2d3748' }}>
                            {docName}
                        </h2>
                        <button
                            onClick={() => setViewingDoc(null)}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '24px',
                                cursor: 'pointer',
                                color: '#94a3b8',
                                padding: '4px 8px',
                                lineHeight: 1,
                            }}
                            aria-label="Zavrieť"
                        >
                            &times;
                        </button>
                    </div>
                    <div style={{
                        padding: '20px',
                        overflowY: 'auto',
                        flex: 1,
                        fontSize: '14px',
                        lineHeight: '1.7',
                        color: '#374151',
                    }} className="legal-doc-overlay">
                        <style>{`
                            .legal-doc-overlay h1 { font-size: 22px; margin-top: 0; margin-bottom: 14px; color: #2c3e50; }
                            .legal-doc-overlay h2 { font-size: 18px; margin-top: 24px; margin-bottom: 10px; color: #2c3e50; }
                            .legal-doc-overlay h3 { font-size: 15px; margin-top: 18px; margin-bottom: 8px; color: #34495e; }
                            .legal-doc-overlay p { margin-bottom: 10px; }
                            .legal-doc-overlay ul, .legal-doc-overlay ol { margin-bottom: 10px; padding-left: 22px; }
                            .legal-doc-overlay li { margin-bottom: 4px; }
                            .legal-doc-overlay table { border-collapse: collapse; width: 100%; margin-bottom: 14px; }
                            .legal-doc-overlay th, .legal-doc-overlay td { border: 1px solid #ddd; padding: 6px 10px; text-align: left; font-size: 13px; }
                            .legal-doc-overlay th { background-color: #f8f9fa; font-weight: 600; }
                            .legal-doc-overlay a { color: #667eea; }
                        `}</style>
                        {docLoading ? (
                            <p style={{ textAlign: 'center', color: '#94a3b8' }}>Načítavam dokument...</p>
                        ) : (
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{docContent}</ReactMarkdown>
                        )}
                    </div>
                    <div style={{
                        padding: '12px 20px',
                        borderTop: '1px solid #e2e8f0',
                        textAlign: 'center',
                        flexShrink: 0,
                    }}>
                        <button
                            onClick={() => setViewingDoc(null)}
                            style={{
                                ...authStyles.button,
                                maxWidth: '200px',
                            }}
                        >
                            Zavrieť
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const stepIndex = { details: 0, password: 1, otp: 2, legal: 3, complete: 4 };
    const steps = ['details', 'password', 'otp', 'legal'] as const;

    const renderStepIndicator = () => (
        <div style={authStyles.stepIndicator}>
            {steps.map((s, i) => (
                <div key={s} style={authStyles.stepDot(step === s, stepIndex[step] > i)} />
            ))}
        </div>
    );

    // Step: Complete (loading/redirect)
    if (step === 'complete') {
        return (
            <div style={authStyles.page}>
                <div style={{ ...authStyles.card, textAlign: 'center' as const }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                    <h2 style={{ ...authStyles.logoTitle, fontSize: '22px' }}>Registrácia úspešná!</h2>
                    <p style={authStyles.subtitle}>Prihlasujem vás...</p>
                    {error && <div style={{ ...authStyles.info, marginTop: '16px' }}>{error}</div>}
                </div>
            </div>
        );
    }

    // Step: OTP verification
    if (step === 'otp') {
        return (
            <div style={authStyles.page}>
                <div style={authStyles.card}>
                    <div style={authStyles.logo}>
                        <div style={authStyles.logoIcon}>📧</div>
                        <h1 style={{ ...authStyles.logoTitle, fontSize: '22px' }}>Overenie e-mailu</h1>
                        <p style={authStyles.subtitle}>
                            Na adresu <strong>{email}</strong> sme vám poslali overovací kód.
                        </p>
                    </div>

                    {renderStepIndicator()}

                    <form onSubmit={handleOtpSubmit} style={authStyles.form}>
                        <div style={authStyles.inputGroup}>
                            <label style={authStyles.label} htmlFor="otp">Overovací kód</label>
                            <input
                                id="otp"
                                type="text"
                                inputMode="numeric"
                                value={otpCode}
                                onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 8))}
                                onFocus={() => setFocusedField('otp')}
                                onBlur={() => setFocusedField(null)}
                                style={{ ...getInputStyle('otp'), textAlign: 'center' as const, letterSpacing: '4px', fontSize: '20px' }}
                                placeholder="------"
                                autoFocus
                                autoComplete="one-time-code"
                                maxLength={8}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !otpCode}
                            style={{
                                ...authStyles.button,
                                ...(loading || !otpCode ? authStyles.buttonDisabled : {}),
                            }}
                        >
                            {loading ? 'Overujem...' : 'Overiť kód'}
                        </button>
                    </form>

                    {error && <div style={{ ...authStyles.error, marginTop: '16px' }}>{error}</div>}

                    <div style={{ ...authStyles.footer as any, fontSize: '13px' }}>
                        Neprišiel vám kód? Skontrolujte priečinok spam.
                    </div>
                </div>
            </div>
        );
    }

    // Step: Password
    if (step === 'password') {
        return (
            <div style={authStyles.page}>
                <div style={authStyles.card}>
                    <div style={authStyles.logo}>
                        <div style={authStyles.logoIcon}>🔒</div>
                        <h1 style={{ ...authStyles.logoTitle, fontSize: '22px' }}>Vytvorte si heslo</h1>
                        <p style={authStyles.subtitle}>
                            Heslo musí mať aspoň 8 znakov, veľké a malé písmená a číslo.
                        </p>
                    </div>

                    {renderStepIndicator()}

                    <form onSubmit={handlePasswordSubmit} style={authStyles.form}>
                        <div style={authStyles.inputGroup}>
                            <label style={authStyles.label} htmlFor="reg-password">Heslo</label>
                            <div style={authStyles.passwordWrapper as any}>
                                <input
                                    id="reg-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => { setPassword(e.target.value.slice(0, 128)); setPasswordError(''); }}
                                    onFocus={() => setFocusedField('password')}
                                    onBlur={() => setFocusedField(null)}
                                    style={getInputStyle('password', !!passwordError)}
                                    placeholder="Zadajte heslo"
                                    autoFocus
                                    autoComplete="new-password"
                                    maxLength={128}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={authStyles.passwordToggle as any}
                                    tabIndex={-1}
                                    aria-label={showPassword ? 'Skryť heslo' : 'Zobraziť heslo'}
                                >
                                    {showPassword ? 'Skryť' : 'Ukázať'}
                                </button>
                            </div>
                            {passwordError && <span style={authStyles.fieldError}>{passwordError}</span>}
                        </div>

                        <div style={authStyles.inputGroup}>
                            <label style={authStyles.label} htmlFor="reg-confirm">Potvrdenie hesla</label>
                            <div style={authStyles.passwordWrapper as any}>
                                <input
                                    id="reg-confirm"
                                    type={showPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value.slice(0, 128))}
                                    onFocus={() => setFocusedField('confirm')}
                                    onBlur={() => setFocusedField(null)}
                                    style={getInputStyle('confirm', !!(confirmPassword && password !== confirmPassword))}
                                    placeholder="Zopakujte heslo"
                                    autoComplete="new-password"
                                    maxLength={128}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={authStyles.passwordToggle as any}
                                    tabIndex={-1}
                                    aria-label={showPassword ? 'Skryť heslo' : 'Zobraziť heslo'}
                                >
                                    {showPassword ? 'Skryť' : 'Ukázať'}
                                </button>
                            </div>
                            {confirmPassword && password !== confirmPassword && (
                                <span style={authStyles.fieldError}>Heslá sa nezhodujú.</span>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !password || !confirmPassword}
                            style={{
                                ...authStyles.button,
                                ...(loading || !password || !confirmPassword ? authStyles.buttonDisabled : {}),
                            }}
                        >
                            {loading ? 'Registrujem...' : 'Zaregistrovať sa'}
                        </button>

                        <button
                            type="button"
                            onClick={() => { setStep('details'); setError(''); setPasswordError(''); }}
                            style={{ ...authStyles.link, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'center' as const, width: '100%' }}
                        >
                            Späť
                        </button>
                    </form>

                    {error && (
                        <div style={{ ...authStyles.error, marginTop: '16px' }}>
                            {error}
                            {error.includes('prihlásiť') && (
                                <div style={{ marginTop: '8px' }}>
                                    <Link to="/auth/login" style={{ ...authStyles.link, color: '#dc2626', textDecoration: 'underline' }}>
                                        Prihlásiť sa
                                    </Link>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Step: Legal acceptance
    if (step === 'legal') {
        return (
            <div style={authStyles.page}>
                {renderDocumentOverlay()}
                <div style={authStyles.card}>
                    <div style={authStyles.logo}>
                        <div style={authStyles.logoIcon}>📋</div>
                        <h1 style={{ ...authStyles.logoTitle, fontSize: '22px' }}>Právne dokumenty</h1>
                        <p style={authStyles.subtitle}>
                            Pre dokončenie registrácie je potrebný súhlas s nasledujúcimi dokumentmi.
                        </p>
                    </div>

                    {renderStepIndicator()}

                    <form onSubmit={handleLegalSubmit} style={authStyles.form}>
                        {/* Mandatory documents */}
                        {MANDATORY_DOCUMENT_TYPES.map((docType) => (
                            <label
                                key={docType}
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '10px',
                                    padding: '12px',
                                    background: acceptedMandatory[docType] ? '#f0fdf4' : '#f8fafc',
                                    borderRadius: '10px',
                                    border: `1px solid ${acceptedMandatory[docType] ? '#bbf7d0' : '#e2e8f0'}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={acceptedMandatory[docType]}
                                    onChange={(e) => setAcceptedMandatory({ ...acceptedMandatory, [docType]: e.target.checked })}
                                    style={{ marginTop: '2px', width: '18px', height: '18px', accentColor: '#667eea' }}
                                />
                                <div>
                                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                                        {DOCUMENT_NAMES[docType]} <span style={{ color: '#ef4444' }}>*</span>
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                        v{LEGAL_DOCUMENTS[docType].currentVersion} —{' '}
                                        <a
                                            href="#"
                                            style={{ color: '#667eea' }}
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openDocument(docType); }}
                                        >
                                            Prečítať celý dokument
                                        </a>
                                    </div>
                                </div>
                            </label>
                        ))}

                        {/* Optional profiling consent */}
                        <label
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px',
                                padding: '12px',
                                background: profilingConsent ? '#eff6ff' : '#f8fafc',
                                borderRadius: '10px',
                                border: `1px solid ${profilingConsent ? '#bfdbfe' : '#e2e8f0'}`,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={profilingConsent}
                                onChange={(e) => setProfilingConsent(e.target.checked)}
                                style={{ marginTop: '2px', width: '18px', height: '18px', accentColor: '#667eea' }}
                            />
                            <div>
                                <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                                    Profilovací súhlas <span style={{ fontSize: '12px', fontWeight: 400, color: '#94a3b8' }}>(voliteľné)</span>
                                </div>
                                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                    Mesačné prehľady, marketingové e-maily a personalizované tipy.
                                    Súhlas je dobrovoľný a kedykoľvek odvolateľný.
                                </div>
                            </div>
                        </label>

                        <button
                            type="button"
                            onClick={() => {
                                setProfilingConsent(true);
                                setAcceptedMandatory({ tos: true, privacy: true, ai_notice: true });
                                setError('');
                                if (signUpState instanceof SignUpCompletedState) {
                                    handleSignUpComplete(signUpState, true);
                                }
                            }}
                            style={{
                                ...authStyles.button,
                                background: '#667eea',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                            }}
                        >
                            Súhlasiť so všetkým a pokračovať
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                setAcceptedMandatory({ tos: true, privacy: true, ai_notice: true });
                                setError('');
                                if (signUpState instanceof SignUpCompletedState) {
                                    handleSignUpComplete(signUpState, false);
                                }
                            }}
                            style={{
                                ...authStyles.button,
                                background: '#667eea',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                            }}
                        >
                            Súhlasiť s povinnými a pokračovať
                        </button>

                    </form>

                    {error && <div style={{ ...authStyles.error, marginTop: '16px' }}>{error}</div>}
                </div>
            </div>
        );
    }

    // Step: Details (initial)
    return (
        <div style={authStyles.page}>
            <div style={authStyles.card}>
                <div style={authStyles.logo}>
                    <div style={authStyles.logoIcon}>📄</div>
                    <h1 style={authStyles.logoTitle}>SmartBloček</h1>
                    <p style={authStyles.subtitle}>Vytvorte si účet</p>
                </div>

                {renderStepIndicator()}

                <form onSubmit={handleDetailsSubmit} style={authStyles.form}>
                    <div style={authStyles.inputGroup}>
                        <label style={authStyles.label} htmlFor="reg-email">E-mail</label>
                        <input
                            id="reg-email"
                            type="email"
                            value={email}
                            onChange={(e) => { setEmail(sanitizeInput(e.target.value, 254)); setEmailError(''); }}
                            onFocus={() => setFocusedField('email')}
                            onBlur={handleEmailBlur}
                            style={getInputStyle('email', !!emailError)}
                            placeholder="vas@email.sk"
                            autoFocus
                            autoComplete="email"
                            maxLength={254}
                        />
                        {emailError && <span style={authStyles.fieldError}>{emailError}</span>}
                    </div>

                    <div style={authStyles.inputGroup}>
                        <label style={authStyles.label} htmlFor="reg-displayname">Prezývka <span style={{ fontWeight: 400, color: '#94a3b8' }}>(ako vás budeme oslovovať)</span></label>
                        <input
                            id="reg-displayname"
                            type="text"
                            value={displayName}
                            onChange={(e) => { setDisplayName(sanitizeInput(e.target.value, 100)); setNameError(''); }}
                            onFocus={() => setFocusedField('displayname')}
                            onBlur={handleNameBlur}
                            style={getInputStyle('displayname', !!nameError)}
                            placeholder="napr. Jano"
                            autoComplete="nickname"
                            maxLength={100}
                        />
                        {nameError && <span style={authStyles.fieldError}>{nameError}</span>}
                    </div>

                    <button
                        type="submit"
                        disabled={!authClient || !!emailError || loading}
                        style={{
                            ...authStyles.button,
                            ...(!authClient || !!emailError || loading ? authStyles.buttonDisabled : {}),
                        }}
                    >
                        {!authClient ? 'Pripájam sa...' : loading ? 'Overujem...' : 'Pokračovať'}
                    </button>

                    <div style={authStyles.divider}>
                        <div style={authStyles.dividerLine} />
                        <span style={authStyles.dividerText}>alebo</span>
                        <div style={authStyles.dividerLine} />
                    </div>

                    <button
                        type="button"
                        style={authStyles.googleButton}
                        disabled={loading || !authClient}
                        onClick={handleGoogleRegister}
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
                            <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                        </svg>
                        Registrovať sa cez Google
                    </button>
                </form>

                {error && <div style={{ ...authStyles.error, marginTop: '16px' }}>{error}</div>}

                <div style={authStyles.footer as any}>
                    Už máte účet?{' '}
                    <Link to="/auth/login" style={authStyles.link}>
                        Prihláste sa
                    </Link>
                </div>
            </div>
        </div>
    );
}
